import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, LayoutGrid, ShieldCheck, Wrench, type LucideIcon } from 'lucide-react';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { api } from '@/lib/api';
import { useMe } from '@/hooks/use-me';
import { useObras } from '@/hooks/use-obras';
import { normText } from '@/lib/client-color';
import { NroRdt } from '@/components/rdt/nro-rdt';
import type { ExpedienteResultado, ExpedientesResponse, ObraCard } from '@/types/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NavAction {
  to: string;
  label: string;
  icon: LucideIcon;
}

const MAX_RESULTS = 10;

/** Resalta (font-semibold) la primera ocurrencia de `query` dentro de
 *  `texto` — highlight simple por substring (sin acentos/mayúsculas vía
 *  normText), suficiente para el ⌘K (resolución #10 del panel adversarial). */
function Resaltado({ texto, query }: { texto: string; query: string }) {
  if (!query) return <>{texto}</>;
  const idx = normText(texto).indexOf(normText(query));
  if (idx === -1) return <>{texto}</>;
  return (
    <>
      {texto.slice(0, idx)}
      <span className="font-semibold">{texto.slice(idx, idx + query.length)}</span>
      {texto.slice(idx + query.length)}
    </>
  );
}

// Command-K global (MVP §5.0 / B.1 punto 3), reconciliado contra el
// artefacto del preview aprobado (2026-07-10, cmdk markup/lógica): además de
// las acciones de navegación, busca en las obras reales (mismo cache
// ['obras'] que la home, vía useObras). shouldFilter={false}: el filtrado
// fuzzy default de cmdk no entiende acentos ni matchea CC/código de obra —
// el filtro propio normaliza y hace un simple `includes` sobre denominación
// + cliente + cc + coordinador + código.
export function GlobalSearch({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { data: user } = useMe();
  const { data: obras } = useObras();
  const rol = user?.rol;
  const isAdmin = rol === 'admin';
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // El artefacto limpia el input cada vez que se abre el buscador.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Debounce ~250ms para la búsqueda de expedientes (server-side, a
  // diferencia del filtro local de obras) — evita un round-trip por tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  function goTo(to: string): void {
    onOpenChange(false);
    navigate(to);
  }

  const navActions: NavAction[] = [
    { to: '/', label: 'Obras', icon: LayoutGrid },
    ...(rol && rol !== 'viewer' ? [{ to: '/gestion', label: 'Gestión', icon: Wrench }] : []),
    ...(isAdmin
      ? [
          { to: '/administracion', label: 'Administración', icon: ShieldCheck },
          { to: '/auditoria', label: 'Auditoría', icon: ClipboardList },
        ]
      : []),
  ];

  const trimmed = query.trim();
  const hasQuery = trimmed.length > 0;
  const normalizedQuery = normText(trimmed);

  // Si la query de obras está cargando o falló, `obras` queda undefined y
  // el grupo "Obras" simplemente no aparece — el buscador sigue funcionando
  // con las acciones de navegación, no se bloquea nada.
  const matchedObras = useMemo((): ObraCard[] => {
    if (!hasQuery || !obras) return [];
    return obras
      .filter(obra => {
        const haystack = normText(
          [
            obra.denominacion,
            obra.cliente ?? '',
            obra.cc,
            obra.coordinador ?? '',
            obra.codigoObra ?? '',
          ].join(' '),
        );
        return haystack.includes(normalizedQuery);
      })
      .slice(0, MAX_RESULTS);
  }, [hasQuery, obras, normalizedQuery]);

  const matchedNavActions = hasQuery
    ? navActions.filter(action => normText(action.label).includes(normalizedQuery))
    : navActions;

  // ⌘K expedientes (resolución #10 del panel adversarial): server-side sobre
  // GET /api/busqueda/expedientes — la referencia NO es única entre RDTs
  // (ej. una misma EX- en varias RDTs de la 1370), así que siempre es lista.
  const expedientesHabilitado = open && debouncedQuery.length >= 2;
  const expedientesQuery = useQuery({
    queryKey: ['busqueda-expedientes', debouncedQuery],
    queryFn: () => api<ExpedientesResponse>(`/api/busqueda/expedientes?q=${encodeURIComponent(debouncedQuery)}`),
    select: (data): ExpedienteResultado[] => data.resultados,
    enabled: expedientesHabilitado,
    staleTime: 30_000,
  });
  const matchedExpedientes = expedientesQuery.data ?? [];

  const noResults =
    hasQuery &&
    matchedObras.length === 0 &&
    matchedNavActions.length === 0 &&
    matchedExpedientes.length === 0 &&
    !expedientesQuery.isFetching;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Buscar"
      description="Buscar obras o expedientes, o navegar por RedeApp"
      className="sm:max-w-lg"
    >
      <Command shouldFilter={false}>
        {/* Anti-autorrelleno best-effort (BACKLOG 11d): iCloud Passwords en
            Chrome/Windows ofrece «Contraseñas en iCloud» al enfocar este
            input vacío (se va al tipear). No hay opt-out documentado para
            esa extensión; se suman las señales que respetan los demás
            gestores (autocomplete/name + los atributos propietarios de
            1Password/LastPass/Bitwarden). Dos intentos de `type="search"`
            (lo que los gestores no tocan) fallaron y NO se insiste por acá:
            (1) por prop no compila — cmdk 1.1.1 excluye `type` de las props
            y fija "text"; (2) por ref (`setAttribute` en un efecto) no
            persiste — React DOM re-asigna `node.type` en CADA update del
            input (`updateInput`), así que vuelve a "text" con la primera
            tecla (verificado con Chrome real, 2026-09-15). La alternativa
            real es un <input type="search"> propio fuera de cmdk.Input,
            que queda en el BACKLOG como decisión de producto. */}
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Buscar obras por CC, nombre, cliente o código — o expedientes por referencia…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          name="redeapp-buscador"
          inputMode="search"
          data-1p-ignore=""
          data-lpignore="true"
          data-bwignore=""
          data-form-type="other"
        />
        <CommandList>
          {noResults && <CommandEmpty>Sin resultados para “{trimmed}”</CommandEmpty>}

          {hasQuery && matchedObras.length > 0 && (
            <CommandGroup heading="Obras">
              {matchedObras.map(obra => (
                <CommandItem
                  key={obra.cc}
                  value={`obra-${obra.cc}`}
                  onSelect={() => goTo(`/obras/${encodeURIComponent(obra.cc)}`)}
                >
                  <span className="min-w-[74px] shrink-0 font-mono text-[12.5px] font-bold">
                    {obra.cc}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px]">{obra.denominacion}</span>
                  {obra.cliente && (
                    <span className="shrink-0 text-[11.5px] text-text-tertiary">{obra.cliente}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {matchedExpedientes.length > 0 && (
            <CommandGroup heading="Expedientes">
              {matchedExpedientes.map(exp => (
                <CommandItem
                  key={`exp-${exp.rdtId}-${exp.referencia}`}
                  value={`exp-${exp.rdtId}-${exp.referencia}`}
                  onSelect={() => goTo(`/obras/${encodeURIComponent(exp.cc)}?rdt=${exp.rdtId}`)}
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-[12.5px] font-bold">
                    <Resaltado texto={exp.referencia} query={trimmed} />
                  </span>
                  <span className="shrink-0 text-[11.5px] text-text-tertiary">
                    {exp.cc} · <NroRdt nro={exp.nro} tipo={exp.tipo} compact className="text-[11.5px] text-text-tertiary" /> {exp.tipo} · {exp.estado}
                    {exp.anterior && <span className="text-text-tertiary"> (anterior)</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {matchedNavActions.length > 0 && (
            <CommandGroup heading="Navegación">
              {matchedNavActions.map(action => {
                const Icon = action.icon;
                return (
                  <CommandItem key={action.to} value={`nav-${action.to}`} onSelect={() => goTo(action.to)}>
                    <Icon />
                    {action.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
